import UtilityService from "./UtilityService";

/**
 * How long one database read is shown for. The server's reads take milliseconds, so every read step
 * ticks within about the first second; the order is real, the timing is cosmetic. 0.2 s keeps the
 * longest read run (a pasted prompt's seven reads) at 1.4 s.
 */
const READ_STEP_SECONDS = 0.2;

/**
 * A playbook guide reply, measured 2026-10-27 from
 * `C:/Users/marri/Projects/workflow/e2e/shots/w1087-builder/eval/*.json` (`turns[].seconds`, n 183).
 */
const PLAYBOOK_TURN_MEDIAN_SECONDS = 49;
/** The p90 of a playbook guide reply (same source, 2026-10-27): past it the wait is called slow. */
const PLAYBOOK_TURN_SLOW_AFTER_SECONDS = 61;
/** The longest playbook guide reply measured (same source, 2026-10-27). */
const PLAYBOOK_TURN_LONGEST_SECONDS = 84;

/**
 * A skill guide reply, measured 2026-10-27 from
 * `C:/Users/marri/Projects/workflow/e2e/shots/w1087-builder/eval/*.json` (`turns[].seconds`, n 49).
 */
const SKILL_TURN_MEDIAN_SECONDS = 44;
/** The p90 of a skill guide reply (same source, 2026-10-27): past it the wait is called slow. */
const SKILL_TURN_SLOW_AFTER_SECONDS = 56;
/** The longest skill guide reply measured (same source, 2026-10-27). */
const SKILL_TURN_LONGEST_SECONDS = 63;

/**
 * A Build of the draft, measured 2026-10-27 from
 * `C:/Users/marri/Projects/workflow/e2e/shots/w1087-builder/eval/*.json` (`buildSeconds`, n 61).
 */
const DRAFT_BUILD_MEDIAN_SECONDS = 19;
/** The p90 of a Build (same source, 2026-10-27): past it the wait is called slow. */
const DRAFT_BUILD_SLOW_AFTER_SECONDS = 34;
/** The longest Build measured (same source, 2026-10-27). */
const DRAFT_BUILD_LONGEST_SECONDS = 69;
/**
 * The share of a Build's wait given to mapping the decisions (`decideDraftSetup` → `mapDecisionPoints`,
 * one AI call); writing the instructions, with its word-limit fittings and statement lines, carries the rest.
 */
const DRAFT_BUILD_DECISIONS_SECONDS = 6;

/**
 * Writing a gap's script, 2026-10-27. There is no timing log for it in
 * `C:/Users/marri/Projects/workflow/e2e/shots/w1087-builder/eval/*.json`; the median and slow mark come
 * from the Scripts card's own promise of "a minute or two", and no longest time is known.
 */
const SCRIPT_WRITING_MEDIAN_SECONDS = 75;
/** The slow mark for writing a script (2026-10-27, from the card's "a minute or two"; no log). */
const SCRIPT_WRITING_SLOW_AFTER_SECONDS = 120;

const BuildProgressService = {
	PLAYBOOK_TURN_MEDIAN_SECONDS,
	PLAYBOOK_TURN_SLOW_AFTER_SECONDS,
	PLAYBOOK_TURN_LONGEST_SECONDS,
	SKILL_TURN_MEDIAN_SECONDS,
	SKILL_TURN_SLOW_AFTER_SECONDS,
	SKILL_TURN_LONGEST_SECONDS,
	DRAFT_BUILD_MEDIAN_SECONDS,
	DRAFT_BUILD_SLOW_AFTER_SECONDS,
	DRAFT_BUILD_LONGEST_SECONDS,
	SCRIPT_WRITING_MEDIAN_SECONDS,
	SCRIPT_WRITING_SLOW_AFTER_SECONDS,

	/**
	 * The steps of a playbook guide reply (or of a pasted prompt), in the server's real order
	 * (api `services/playbookBuildGuideService.js`, `sendMessage` 221-409; `importPrompt` 487-497):
	 * - `savePrompt` (490): the pasted text is saved before the guide is asked; only for a pasted prompt;
	 * - `read` (222): the conversation is loaded;
	 * - `library` (225-227): the guide skill, the firm's options with the source catalogue, its playbooks;
	 * - `tests` (228): the last three test runs; only when the conversation has one;
	 * - `files` (229): the reference files; the guide sees each name and opening only
	 *   (`playbookBuildService.js:561-565`), so the step notes them, never reads them;
	 * - `scripts` (235-236): the firm's script library; only when scripts are on;
	 * - `prompt` (253): the pasted prompt is included; only when the conversation holds one;
	 * - `think` (277-283 the answer, 312-322 at most one re-ask when "ready too soon", 345-354 the skill
	 *   check unless the guide drafted a skill): one to three AI calls, none foreseeable, so one step.
	 * The saves (368, 390-407) and the re-read (408) take milliseconds and are not shown.
	 * Every read gets `READ_STEP_SECONDS`; `think` carries the rest of the measured median.
	 *
	 * @param {Object} params
	 * @param {Object} params.session the build session: { testRuns, referenceFiles, sourceCatalogue, hasImportedPrompt }
	 * @param {Boolean} params.areScriptsOn whether the firm's scripts are on for this viewer
	 * @param {Boolean} [params.isPastedPrompt] whether this request is a pasted prompt being imported
	 * @return {Array<Object>} [{ key, label, expectedSeconds }]
	 */
	playbookTurnSteps({ session = {}, areScriptsOn = false, isPastedPrompt = false }) {
		const { testRuns = [], referenceFiles = [], sourceCatalogue = [], hasImportedPrompt = false } = session;

		const sourceCount = sourceCatalogue.length;
		const sourcesWords = sourceCount > 0 ? UtilityService.describeCount({ count: sourceCount, singular: "source" }) : "sources";
		const hasTestRun = testRuns.length > 0;
		const lastTestClientName = hasTestRun && testRuns[0].client ? testRuns[0].client.name : null;
		const testsLabel = lastTestClientName ? `Looking at the last test, run for ${lastTestClientName}` : "Looking at the last test runs";

		const hasFiles = referenceFiles.length > 0;
		const fileNames = referenceFiles.map(file => file.fileName);
		const namedFileNames = fileNames.slice(0, 2);
		const unnamedFileCount = fileNames.length - namedFileNames.length;
		const namedFilesWords = namedFileNames.join(" and ");
		const namedFilesList = namedFileNames.join(", ");
		const filesWords = unnamedFileCount > 0 ? `${namedFilesList} and ${unnamedFileCount} more` : namedFilesWords;
		const fileNoun = fileNames.length === 1 ? "file" : "files";
		const filesLabel = `Noting your ${fileNoun} ${filesWords}`;

		const isPromptIncluded = hasImportedPrompt || isPastedPrompt;

		const savePromptStep = { key: "savePrompt", label: "Saving the prompt you pasted", expectedSeconds: READ_STEP_SECONDS };
		const readStep = { key: "read", label: "Reading your message and the brief so far", expectedSeconds: READ_STEP_SECONDS };
		const libraryStep = { key: "library", label: `Looking through the firm's playbooks, skills and ${sourcesWords}`, expectedSeconds: READ_STEP_SECONDS };
		const testsStep = { key: "tests", label: testsLabel, expectedSeconds: READ_STEP_SECONDS };
		const filesStep = { key: "files", label: filesLabel, expectedSeconds: READ_STEP_SECONDS };
		const scriptsStep = { key: "scripts", label: "Checking the firm's scripts", expectedSeconds: READ_STEP_SECONDS };
		const promptStep = { key: "prompt", label: "Including the prompt you pasted", expectedSeconds: READ_STEP_SECONDS };

		const possibleReadSteps = [
			isPastedPrompt ? savePromptStep : null,
			readStep,
			libraryStep,
			hasTestRun ? testsStep : null,
			hasFiles ? filesStep : null,
			areScriptsOn ? scriptsStep : null,
			isPromptIncluded ? promptStep : null,
		];
		const readSteps = possibleReadSteps.filter(Boolean);
		const readSeconds = readSteps.reduce((total, step) => total + step.expectedSeconds, 0);

		const thinkStep = { key: "think", label: "Working out what to ask you next", expectedSeconds: PLAYBOOK_TURN_MEDIAN_SECONDS - readSeconds };
		const steps = [...readSteps, thinkStep];
		return steps;
	},

	/**
	 * The steps of a Build, in the server's real order (api `services/playbookBuildDraftService.js`,
	 * `buildDraft` 237-473):
	 * - `read` (238): the conversation is loaded;
	 * - `scripts` (243): the scripts written for its steps; only when a gap is scripted;
	 * - `library` (249-250): the prompt-writing skill and the firm's options;
	 * - `previous` (253-255): the previous version and its statement lines; only when a draft exists;
	 * - `files` (254): the reference files, noted by name and opening (the guide reply's own `files` step);
	 * - `write` (334-343 the instructions, then 0-3 word-limit fittings, then 0-1 statement-lines call at
	 *   356): not foreseeable, so one step worded to cover them;
	 * - `decisions` (371-381 `decideDraftSetup` → `mapDecisionPoints`, always, plus 0-1 tracked-fields call).
	 * The saves and the re-read are not shown. The next draft number is the playbook's highest version
	 * plus one (402-407): `draft.versionNumber + 1` when a draft exists, 1 for a new playbook, otherwise
	 * no number is named. `decisions` gets `DRAFT_BUILD_DECISIONS_SECONDS`; `write` carries the rest.
	 *
	 * @param {Object} params
	 * @param {Object} params.session the build session: { brief, draft, playbookId, gaps, referenceFiles, sourceCatalogue }
	 * @return {Array<Object>} [{ key, label, expectedSeconds }]
	 */
	draftBuildSteps({ session = {} }) {
		const { brief = null, draft = null, playbookId = null, gaps = [], sourceCatalogue = [] } = session;

		const briefName = brief ? brief.name : null;
		const draftName = draft ? draft.name : null;
		const playbookName = briefName || draftName || null;
		const hasDraft = Boolean(draft);
		const isNewPlaybook = !playbookId;
		const previousDraftNumber = hasDraft ? draft.versionNumber : null;
		const firstDraftNumber = isNewPlaybook ? 1 : null;
		const nextDraftNumber = hasDraft ? previousDraftNumber + 1 : firstDraftNumber;

		const draftWords = nextDraftNumber ? `draft ${nextDraftNumber}` : "the draft";
		const writeLabel = playbookName ? `Writing ${draftWords} of the instructions for ${playbookName}` : `Writing ${draftWords} of the playbook's instructions`;
		const readLabel = playbookName ? `Reading the conversation and the brief for ${playbookName}` : "Reading the conversation and the brief";

		const hasScriptedGap = gaps.some(gap => gap.status === "scripted");
		const sourceCount = sourceCatalogue.length;
		const sourcesWords = sourceCount > 0 ? UtilityService.describeCount({ count: sourceCount, singular: "source" }) : "sources";

		const guideSteps = BuildProgressService.playbookTurnSteps({ session });
		const filesStep = guideSteps.find(step => step.key === "files") || null;

		const readStep = { key: "read", label: readLabel, expectedSeconds: READ_STEP_SECONDS };
		const scriptsStep = { key: "scripts", label: "Gathering the scripts written for its steps", expectedSeconds: READ_STEP_SECONDS };
		const libraryStep = { key: "library", label: `Looking through the firm's skills and ${sourcesWords}`, expectedSeconds: READ_STEP_SECONDS };
		const previousStep = { key: "previous", label: `Looking at draft ${previousDraftNumber} and its statement lines`, expectedSeconds: READ_STEP_SECONDS };

		const possibleReadSteps = [readStep, hasScriptedGap ? scriptsStep : null, libraryStep, hasDraft ? previousStep : null, filesStep];
		const readSteps = possibleReadSteps.filter(Boolean);
		const readSeconds = readSteps.reduce((total, step) => total + step.expectedSeconds, 0);
		const writeSeconds = DRAFT_BUILD_MEDIAN_SECONDS - readSeconds - DRAFT_BUILD_DECISIONS_SECONDS;

		const writeStep = { key: "write", label: writeLabel, expectedSeconds: writeSeconds };
		const decisionsStep = { key: "decisions", label: "Mapping the decisions it records on every run", expectedSeconds: DRAFT_BUILD_DECISIONS_SECONDS };
		const steps = [...readSteps, writeStep, decisionsStep];
		return steps;
	},

	/**
	 * The steps of writing a gap's script, in the server's real order (api
	 * `services/playbookScriptDraftService.js`, `draftScript` 196-331):
	 * - `read` (198): the conversation, its pins and the gap's script;
	 * - `previous` (244-279): the current revision, its tests and review findings; only for a redraft
	 *   (the gap already has a script);
	 * - `names` (226-242): the script names already taken; only for a new script;
	 * - `write` (304, plus 0-1 retry at 137): the AI call, carrying the rest of the median.
	 * The saves are not shown.
	 *
	 * @param {Object} params
	 * @param {Object} params.gap the gap being scripted: { step, scriptId }
	 * @return {Array<Object>} [{ key, label, expectedSeconds }]
	 */
	scriptWritingSteps({ gap = {} }) {
		const { step = null, scriptId = null } = gap;

		const isRedraft = Boolean(scriptId);
		const stepWords = step ? `the step “${step}”` : "this step";

		const readStep = { key: "read", label: `Reading the conversation about ${stepWords}`, expectedSeconds: READ_STEP_SECONDS };
		const previousStep = { key: "previous", label: "Looking at the current script, its tests and review notes", expectedSeconds: READ_STEP_SECONDS };
		const namesStep = { key: "names", label: "Checking which script names are already taken", expectedSeconds: READ_STEP_SECONDS };
		const readSteps = [readStep, isRedraft ? previousStep : namesStep];
		const readSeconds = readSteps.reduce((total, readingStep) => total + readingStep.expectedSeconds, 0);

		const writeStep = { key: "write", label: `Writing the script for ${stepWords}`, expectedSeconds: SCRIPT_WRITING_MEDIAN_SECONDS - readSeconds };
		const steps = [...readSteps, writeStep];
		return steps;
	},

	/**
	 * The steps of a skill guide reply, in the server's real order (api `services/skillBuildService.js`,
	 * `sendMessage` 136-200):
	 * - `read` (137): the conversation is loaded;
	 * - `library` (140-141): the guide skill and the firm's options;
	 * - `think` (172-178 the answer, plus 0-1 "ask again for the draft" at 183): one step.
	 * No files, tests or scripts are read in this request.
	 *
	 * @param {Object} params
	 * @param {Object} params.session the skill build session: { sourceCatalogue }
	 * @return {Array<Object>} [{ key, label, expectedSeconds }]
	 */
	skillTurnSteps({ session = {} }) {
		const { sourceCatalogue = [] } = session;

		const sourceCount = sourceCatalogue.length;
		const sourcesWords = sourceCount > 0 ? UtilityService.describeCount({ count: sourceCount, singular: "source" }) : "sources";

		const readStep = { key: "read", label: "Reading your message and the skill so far", expectedSeconds: READ_STEP_SECONDS };
		const libraryStep = { key: "library", label: `Looking through the firm's skills and ${sourcesWords}`, expectedSeconds: READ_STEP_SECONDS };
		const readSteps = [readStep, libraryStep];
		const readSeconds = readSteps.reduce((total, step) => total + step.expectedSeconds, 0);

		const thinkStep = { key: "think", label: "Working out what to ask you next", expectedSeconds: SKILL_TURN_MEDIAN_SECONDS - readSeconds };
		const steps = [...readSteps, thinkStep];
		return steps;
	},

	/**
	 * Which steps are done, which one is current and which are still to come after `elapsedSeconds`,
	 * walking the steps by their cumulative `expectedSeconds`. The last step never becomes done: it
	 * stays current until the answer arrives, however long that takes.
	 *
	 * @param {Object} params
	 * @param {Array<Object>} params.steps [{ key, label, expectedSeconds }]
	 * @param {Number} params.elapsedSeconds seconds since the request was sent
	 * @param {Number} params.slowAfterSeconds the measured slow mark (p90) for this action
	 * @return {Object} { steps: [{ key, label, expectedSeconds, state: "done" | "current" | "upcoming" }], isSlow }
	 */
	progressAt({ steps = [], elapsedSeconds = 0, slowAfterSeconds = Infinity }) {
		const lastIndex = steps.length - 1;
		const startSeconds = steps.map((step, index) => {
			const earlierSteps = steps.slice(0, index);
			const secondsBefore = earlierSteps.reduce((total, earlierStep) => total + earlierStep.expectedSeconds, 0);
			return secondsBefore;
		});

		const stepsWithState = steps.map((step, index) => {
			const startedAt = startSeconds[index];
			const endsAt = startedAt + step.expectedSeconds;
			const isLastStep = index === lastIndex;
			const hasStarted = elapsedSeconds >= startedAt;
			const hasEnded = !isLastStep && elapsedSeconds >= endsAt;
			const stateOnceNotDone = hasStarted ? "current" : "upcoming";
			const state = hasEnded ? "done" : stateOnceNotDone;
			const stepWithState = { ...step, state };
			return stepWithState;
		});

		const isSlow = elapsedSeconds > slowAfterSeconds;
		return { steps: stepsWithState, isSlow };
	},
};

export default BuildProgressService;
